import React, { useEffect, useState } from 'react';
import { Form, Button, InputGroup } from 'react-bootstrap';
import {
    makeStyles,
    CircularProgress,
} from '@material-ui/core';
import axios from 'axios';
import { isEmpty } from 'lodash';
import ModalUtils from 'utils/ModalUtils';
import { makeUseAxios } from 'axios-hooks';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import { useHistory, useParams } from 'react-router';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import MainLayout from 'components/modules/login/MainLayout';
import FormattedTooltip from 'components/widgets/FormattedTooltip';
import LoginStyles from 'styles/modules/LoginStyles';
import { PASSWORD_RULES, validatePassword } from 'utils/PasswordValidation';

const useStyles = makeStyles((theme) => ({
    title: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.43,
        color: theme.palette.text.white,
        marginBottom: '8px',
        display: 'block',
        textAlign: 'center',
    },
    inputGroup: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.background.white,
        borderRadius: '4px',
        '& input': {
            border: 'none',
        },
        '& input:focus': {
            outline: 'none',
            borderColor: 'transparent',
            boxShadow: 'none',
        },
        '& span': {
            border: 'none',
            background: 'none',
        },
    },
    passwordTooltip: {
        maxWidth: 360,
        border: `1px solid ${theme.palette.text.curiousBlue}`,
        boxShadow: '0 4px 16px rgba(15, 23, 42, 0.12)',
    },
    passwordRules: {
        display: 'flex',
        flexDirection: 'column',
    },
    passwordRule: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 0),
        borderBottom: `1px solid ${theme.palette.border.ghost}`,
        fontSize: 13,
        lineHeight: 1.4,
        color: theme.palette.text.waterloo,
        '&:first-child': {
            paddingTop: 0,
        },
        '&:last-child': {
            borderBottom: 'none',
            paddingBottom: 0,
        },
    },
    passwordRuleIconPass: {
        fontSize: 18,
        color: theme.palette.background.fruitSalad,
        flexShrink: 0,
        marginTop: 1,
    },
    passwordRuleIconFail: {
        fontSize: 18,
        color: theme.palette.text.statusGray,
        flexShrink: 0,
        marginTop: 1,
    },
    ...LoginStyles.login(theme),
}));

const useAxios = makeUseAxios({
    axios: axios.create({ baseURL: process.env.REACT_APP_GRAPHQL_HTTP }),
});

const PasswordReset = () => {
    const [confirmPassword, setConfirmPassword] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordTooltipOpen, setPasswordTooltipOpen] = useState(false);
    const [userInformation, setUserInformation] = useState({});
    const classes = useStyles();
    const { token } = useParams();
    const history = useHistory();

    const [{ loading, data, error }, getUserInformation] = useAxios(
        { url: `/password/validate-data?token=${encodeURIComponent(token)}`, method: 'POST' },
        { manual: true },
    );
    const [{ loading: loadingForgotPassword }, forgotPassword] = useAxios(
        { url: '/password/reset', method: 'POST' },
        { manual: true },
    );

    const profileReady = !loading && !error && !isEmpty(userInformation);

    const { valid, validation } = validatePassword(
        password,
        confirmPassword,
        userInformation,
    );
    // Until profile loads, treat personal-information as unmet so submit stays blocked.
    const validationWithProfile = {
        ...validation,
        personalInformation: profileReady && validation.personalInformation,
    };
    const canSubmit = valid && profileReady && !loadingForgotPassword;

    const handleLogin = async (event) => {
        event.preventDefault();
        try {
            if (!canSubmit) return;

            const result = await forgotPassword({ data: { password, token } });
            if (result?.data?.Success) {
                ModalUtils.successMessage([], result.data.Message);
                history.push('/login');
            } else {
                ModalUtils.errorMessage(result?.data?.Messages);
            }
        } catch (requestError) {
            ModalUtils.errorMessage(requestError?.response?.data?.Messages);
        }
    };

    useEffect(() => {
        if (!isEmpty(data)) {
            setUserInformation({
                firstName: data.FirstName,
                lastName: data.LastName,
                birthday: data.DOB,
                email: data.Email,
                workEmail: data.WorkEmail,
                nationality: data.Nationality,
            });
        }
    }, [loading, data]);

    useEffect(() => {
        if (token) {
            getUserInformation();
        }
    }, [getUserInformation, token]);

    const passwordRequirementsBody = (
        <div className={classes.passwordRules}>
            {PASSWORD_RULES.map((rule) => {
                const passed = Boolean(validationWithProfile[rule.key]);
                return (
                    <div key={rule.key} className={classes.passwordRule}>
                        {passed ? (
                            <CheckCircleIcon className={classes.passwordRuleIconPass} />
                        ) : (
                            <CancelIcon className={classes.passwordRuleIconFail} />
                        )}
                        <span>{rule.label}</span>
                    </div>
                );
            })}
        </div>
    );

    return (
        <MainLayout>
            <Form onSubmit={handleLogin}>
                <span className={classes.title}>Please enter your new password.</span>
                <Form.Group controlId="formPassword">
                    <Form.Label>New Password</Form.Label>
                    <FormattedTooltip
                        header="Password Requirements:"
                        body={passwordRequirementsBody}
                        placement="left"
                        open={passwordTooltipOpen}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        interactive
                        classes={{ tooltip: classes.passwordTooltip }}
                    >
                        <InputGroup className={classes.inputGroup}>
                            <Form.Control
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Password"
                                required
                                value={password}
                                autoComplete="new-password"
                                onFocus={() => setPasswordTooltipOpen(true)}
                                onBlur={() => setPasswordTooltipOpen(false)}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <InputGroup.Text onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </InputGroup.Text>
                        </InputGroup>
                    </FormattedTooltip>
                </Form.Group>
                <Form.Group controlId="confirmPassword">
                    <Form.Label>Confirm Password</Form.Label>
                    <InputGroup className={classes.inputGroup}>
                        <Form.Control
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="Confirm Password"
                            required
                            value={confirmPassword}
                            autoComplete="new-password"
                            onFocus={() => setPasswordTooltipOpen(true)}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                        <InputGroup.Text onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                            {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </InputGroup.Text>
                    </InputGroup>
                </Form.Group>
                <Button variant="primary" type="submit" disabled={!canSubmit}>
                    {loadingForgotPassword ? <CircularProgress size="1em" color="inherit" />
                        : (
                            <svg
                                viewBox="64 64 896 896"
                                focusable="false"
                                data-icon="login"
                                width="1em"
                                height="1em"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                {/* eslint-disable-next-line max-len */}
                                <path d="M521.7 82c-152.5-.4-286.7 78.5-363.4 197.7-3.4 5.3.4 12.3 6.7 12.3h70.3c4.8 0 9.3-2.1 12.3-5.8 7-8.5 14.5-16.7 22.4-24.5 32.6-32.5 70.5-58.1 112.7-75.9 43.6-18.4 90-27.8 137.9-27.8 47.9 0 94.3 9.3 137.9 27.8 42.2 17.8 80.1 43.4 112.7 75.9 32.6 32.5 58.1 70.4 76 112.5C865.7 417.8 875 464.1 875 512c0 47.9-9.4 94.2-27.8 137.8-17.8 42.1-43.4 80-76 112.5s-70.5 58.1-112.7 75.9A352.8 352.8 0 0 1 520.6 866c-47.9 0-94.3-9.4-137.9-27.8A353.84 353.84 0 0 1 270 762.3c-7.9-7.9-15.3-16.1-22.4-24.5-3-3.7-7.6-5.8-12.3-5.8H165c-6.3 0-10.2 7-6.7 12.3C234.9 863.2 368.5 942 520.6 942c236.2 0 428-190.1 430.4-425.6C953.4 277.1 761.3 82.6 521.7 82zM395.02 624v-76h-314c-4.4 0-8-3.6-8-8v-56c0-4.4 3.6-8 8-8h314v-76c0-6.7 7.8-10.5 13-6.3l141.9 112a8 8 0 0 1 0 12.6l-141.9 112c-5.2 4.1-13 .4-13-6.3z" />
                            </svg>
                        )}
                    <span className="icon">Reset Password</span>
                </Button>
            </Form>
        </MainLayout>
    );
};

export default PasswordReset;
